<script lang="ts">
  /**
   * DateNavHeader — the presentational ‹ date › chrome, extracted from
   * CalendarHeader so the Club can reuse the exact same shell with a different
   * nav model (sessions, not my-workout UUIDs). Pure/prop-driven: no stores.
   */
  import { chooseDateText } from "../lib/format";
  import { watchDateFit } from "../lib/dateFit";

  interface Props {
    dateText: string;
    /** The same date with the weekday abbreviated; shown only when `dateText` would not fit
     *  on one line (B-022, Scott 2026-09-13: "Today · Wed, Sep 9"). */
    shortDateText?: string | null;
    canPrev: boolean;
    canNext: boolean;
    onPrev: () => void;
    onNext: () => void;
    prevLabel: string;
    nextLabel: string;
    /** When set, the center is a tappable calendar button; else a static date. */
    onOpenCalendar?: (() => void) | null;
    calendarLabel?: string;
    /** Told after every fit whether the row shows the short label, so a card under the row can
     *  use the same format (B-031). */
    onFit?: ((abbreviated: boolean) => void) | null;
  }
  let {
    dateText,
    shortDateText = null,
    canPrev,
    canNext,
    onPrev,
    onNext,
    prevLabel,
    nextLabel,
    onOpenCalendar = null,
    calendarLabel = "Open calendar",
    onFit = null,
  }: Props = $props();

  // B-022: measure the full label on one line (a hidden twin) against the width the date
  // slot leaves after its padding and the calendar icon; swap to the short label only when
  // it would wrap. B-030: re-measured when the web font lands, on resize and when the slot
  // resizes (watchDateFit), so a cold launch never keeps the fallback font's answer.
  let slotEl = $state<HTMLElement | null>(null);
  let measureEl = $state<HTMLSpanElement | null>(null);
  let shownText = $state("");
  $effect(() => {
    const full = dateText;
    const short = shortDateText;
    const report = onFit;
    shownText = full;
    const slot = slotEl;
    const measure = measureEl;
    if (!slot || !measure || !short) {
      report?.(false);
      return;
    }
    const fit = () => {
      const cs = getComputedStyle(slot);
      const icon = slot.querySelector<HTMLElement>(".cal-icon");
      const gap = icon ? parseFloat(cs.columnGap) || 0 : 0;
      const available = slot.clientWidth - parseFloat(cs.paddingLeft) - parseFloat(cs.paddingRight) - (icon?.getBoundingClientRect().width ?? 0) - gap;
      const chosen = chooseDateText(full, short, measure.getBoundingClientRect().width, available);
      shownText = chosen;
      report?.(chosen !== full);
    };
    return watchDateFit(fit, { fonts: document.fonts ?? null, win: window, slot });
  });
</script>

<div class="cal-header">
  <span class="date date-measure" aria-hidden="true" bind:this={measureEl}>{dateText}</span>
  <button
    type="button"
    class="nav-arrow"
    aria-label={prevLabel}
    disabled={!canPrev}
    onclick={onPrev}
  >‹</button>

  {#if onOpenCalendar}
    <button
      type="button"
      class="cal-open"
      data-testid="calendar-header"
      aria-label={calendarLabel}
      onclick={onOpenCalendar}
      bind:this={slotEl}
    >
      <span class="date">{shownText || dateText}</span>
      <span class="cal-icon" aria-hidden="true">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <rect x="3" y="4" width="18" height="17" rx="2" />
          <line x1="3" y1="9" x2="21" y2="9" />
          <line x1="8" y1="2" x2="8" y2="6" />
          <line x1="16" y1="2" x2="16" y2="6" />
        </svg>
      </span>
    </button>
  {:else}
    <span class="cal-open cal-static" bind:this={slotEl}>
      <span class="date">{shownText || dateText}</span>
    </span>
  {/if}

  <button
    type="button"
    class="nav-arrow"
    aria-label={nextLabel}
    disabled={!canNext}
    onclick={onNext}
  >›</button>
</div>

<style>
  .cal-header {
    position: relative;
    display: flex;
    align-items: center;
    gap: var(--space-1);
    background: var(--bg-card);
    border-radius: var(--radius-card);
    min-height: 48px;
  }
  .nav-arrow {
    flex: 0 0 auto;
    width: 44px;
    height: 44px;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 0;
    border-radius: var(--radius-pill);
    background: var(--bg-raised);
    color: var(--accent-primary);
    font-family: var(--font-text);
    font-size: 24px;
    line-height: 1;
    cursor: pointer;
  }
  .nav-arrow:disabled {
    /* text-dim (#5C5C5C) on bg-raised was 2.1:1 — below even the 3:1 large-text
       floor. text-muted clears contrast on this surface (DESIGN.md: dim is
       decoration-tier only, never for content someone must read). No `opacity`
       here on purpose: this button sits inside the Dawn Glass header, and a
       CSS `opacity` blends the WHOLE subtree (glyph + its opaque bg-raised
       fill together) toward the sunrise photo behind it, undoing any color
       fix — both nav arrows render disabled at initial paint (before
       loadWorkouts resolves), so this hit every cold load. The muted color
       alone carries the disabled signal. */
    color: var(--text-muted);
    cursor: default;
  }
  .nav-arrow:active:not(:disabled) {
    background: var(--accent-primary);
    color: var(--bg-canvas);
  }
  .cal-open {
    flex: 1 1 auto;
    min-width: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: var(--space-2);
    min-height: 48px;
    padding: 0 var(--space-2);
    border: 0;
    background: transparent;
    cursor: pointer;
    font-family: var(--font-text);
    border-radius: var(--radius-button);
  }
  .cal-open:active:not(.cal-static) {
    /* Only the tappable calendar button gets the press feedback; the static Club
       date must not flash on tap. */
    background: var(--bg-raised);
  }
  .cal-static {
    cursor: default;
  }
  .date {
    font-size: 17px;
    font-weight: 600;
    color: var(--text-primary);
    /* B-022: one line always; the short label covers a narrow row, an ellipsis only if even it cannot fit. */
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    min-width: 0;
  }
  .date-measure {
    position: absolute;
    visibility: hidden;
    pointer-events: none;
    left: 0;
    top: 0;
    overflow: visible;
  }
  .cal-icon {
    display: flex;
    color: var(--accent-primary);
  }
</style>
